/**
 * Derivacion de saldos desde el libro (INV-4): todo saldo es una funcion pura
 * de los asientos, nunca un valor almacenado independiente. Recalcular desde
 * cero siempre reproduce el mismo resultado.
 */

import type { Account } from "./account.js";
import type { Entry } from "./entry.js";
import { addMoney, money, type Money } from "./money.js";

/** Saldo directo de una cuenta (sin agregar descendientes). */
export function accountBalance(
  accountId: string,
  entries: readonly Entry[],
  currency: string,
  scale: number,
): Money {
  let total = money(0n, currency, scale);
  for (const entry of entries) {
    for (const posting of entry.postings) {
      if (posting.accountId === accountId && posting.currency === currency) {
        total = addMoney(total, money(posting.amountMinor, posting.currency, posting.scale));
      }
    }
  }
  return total;
}

/**
 * Saldo agregado de una cuenta padre: su propio saldo mas el de todos sus
 * descendientes (FR-ACCT-3). `accounts` debe incluir toda la jerarquia.
 */
export function aggregatedBalance(
  accountId: string,
  accounts: readonly Account[],
  entries: readonly Entry[],
  currency: string,
  scale: number,
): Money {
  const descendantIds = collectDescendantIds(accountId, accounts);
  descendantIds.add(accountId);
  let total = money(0n, currency, scale);
  for (const entry of entries) {
    for (const posting of entry.postings) {
      if (descendantIds.has(posting.accountId) && posting.currency === currency) {
        total = addMoney(total, money(posting.amountMinor, posting.currency, posting.scale));
      }
    }
  }
  return total;
}

function collectDescendantIds(rootId: string, accounts: readonly Account[]): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const acc of accounts) {
    if (acc.parentId !== null) {
      const list = childrenByParent.get(acc.parentId) ?? [];
      list.push(acc.accountId);
      childrenByParent.set(acc.parentId, list);
    }
  }
  const result = new Set<string>();
  const stack = [...(childrenByParent.get(rootId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (result.has(id)) continue; // proteccion frente a ciclos
    result.add(id);
    stack.push(...(childrenByParent.get(id) ?? []));
  }
  return result;
}

/** Cantidad de un instrumento en una cuenta de inversion, derivada del libro (FR-INVEST-2). */
export function instrumentQuantity(accountId: string, entries: readonly Entry[]): bigint {
  let total = 0n;
  for (const entry of entries) {
    for (const posting of entry.postings) {
      if (posting.accountId === accountId && posting.quantityMinor !== null) {
        total += posting.quantityMinor;
      }
    }
  }
  return total;
}

/** Coste base de una posicion: suma del dinero pagado (importe con signo) al adquirirla, derivado del libro. */
export function costBasis(
  accountId: string,
  entries: readonly Entry[],
  currency: string,
  scale: number,
): Money {
  return accountBalance(accountId, entries, currency, scale);
}
